import { useState, useEffect, useCallback } from 'react';
import Fuse from 'fuse.js';
import { loadBooksData } from '../services/booksData.js';
import { getCoverUrl, fetchCoverMapping } from '../utils/coverResolver';

const normalize = (text) => {
  return String(text || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');
};

const mapBook = (book, coverMapping = {}) => {
  const extractedCover = getCoverUrl(book, coverMapping);
  const cover = extractedCover || book.image;
  return {
    ...book,
    titre: book.title,
    auteur_nom: book.author,
    categorie_nom: book.category,
    annee_publication: book.year,
    editeur: book.publisher,
    resume: book.description,
    image_couverture: cover,
    image: cover,
    exemplaires_disponibles: book.qty,
    nombre_exemplaires: book.qty,
    statut: book.qty > 0 ? 'disponible' : 'indisponible',
    langue: 'Francais',
    nombre_pages: 'N/A',
    isbn: 'N/A',
  };
};

export const useBooks = (filters = {}) => {
  const [books, setBooks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState({ total: 0, pages: 1 });
  const [coverMapping, setCoverMapping] = useState({});

  useEffect(() => {
    fetchCoverMapping().then(setCoverMapping);
  }, []);

  useEffect(() => {
    loadBooks();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(filters), coverMapping]);

  const loadBooks = async () => {
    try {
      setLoading(true);
      setError(null);

      const booksData = await loadBooksData();

      const mapWithCovers = (b) => mapBook(b, coverMapping);
      let filtered = booksData.map(mapWithCovers);

      if (filters.search) {
        const fuse = new Fuse(filtered, {
          keys: [
            { name: 'titre', weight: 0.7 },
            { name: 'auteur_nom', weight: 0.5 },
            { name: 'categorie_nom', weight: 0.3 },
            { name: 'resume', weight: 0.1 },
          ],
          threshold: 0.3,
          includeScore: true,
          // Normalize to improve match quality with accents/casing.
          getFn: (obj, path) => normalize(obj[path]),
        });

        const results = fuse.search(filters.search);
        filtered = results.map((result) => result.item);
      }

      if (filters.categorie_id) {
        filtered = filtered.filter((b) => b.categorie_nom === filters.categorie_id);
      }

      setBooks(filtered);
      setPagination({
        total: filtered.length,
        pages: 1,
        current_page: 1,
        per_page: filtered.length,
      });
    } catch (err) {
      console.error('Error loading books:', err);
      setError(err?.message || String(err));
      setBooks([]);
    } finally {
      setLoading(false);
    }
  };

  const getBook = useCallback(async (id) => {
    try {
      const numId = Number(id);
      if (Number.isNaN(numId)) throw new Error('Livre non trouve');
      const booksData = await loadBooksData();
      const book = booksData.find((b) => Number(b.id) === numId);
      if (!book) throw new Error('Livre non trouve');
      const mapping = await fetchCoverMapping();
      return mapBook(book, mapping);
    } catch (err) {
      console.error('Error getting book:', err);
      throw err;
    }
  }, []);

  const createBook = async () => {};
  const updateBook = async () => {};
  const deleteBook = async () => {};

  return {
    books,
    loading,
    error,
    pagination,
    loadBooks,
    getBook,
    createBook,
    updateBook,
    deleteBook,
  };
};

export const useAuthors = () => {
  const [authors, setAuthors] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      const booksData = await loadBooksData();
      if (cancelled) return;

      const uniqueAuthors = [...new Set(booksData.map((b) => b.author))]
        .map((name) => ({
          id: name,
          nom: name,
        }))
        .sort((a, b) => a.nom.localeCompare(b.nom));

      setAuthors(uniqueAuthors);
      setLoading(false);
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  return { authors, loading, loadAuthors: () => {} };
};

export const useCategories = () => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      const booksData = await loadBooksData();
      if (cancelled) return;

      const uniqueCats = [...new Set(booksData.map((b) => b.category))]
        .map((name) => ({
          id: name,
          nom: name,
        }))
        .sort((a, b) => a.nom.localeCompare(b.nom));

      setCategories(uniqueCats);
      setLoading(false);
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  return { categories, loading, loadCategories: () => {} };
};

export const useAdminBooks = useBooks;

